import { trackJC } from '../../utils/analytics';

const LEARN_MODULE_IDS = [
  'scale-practice',
  'major-pentatonic-scale',
  'blues-scale-all',
  'diatonic-scale-all',
  'triads-practice',
  'triad-shapes-ead',
  'triad-shapes-adg',
  'triad-shapes-dgb',
  'triad-shapes-gbe',
  'triad-shapes-min-ead',
  'triad-shapes-min-adg',
  'triad-shapes-min-dgb',
  'triad-shapes-min-gbe',
  'triad-shapes-aug-ead',
  'triad-shapes-aug-adg',
  'triad-shapes-aug-dgb',
  'triad-shapes-aug-gbe',
  'triad-shapes-dim-ead',
  'triad-shapes-dim-adg',
  'triad-shapes-dim-dgb',
  'triad-shapes-dim-gbe',
  'guided-chord-progression',
  'guided-solo',
  'tab-practice',
] as const;

export type LearnModuleId = (typeof LEARN_MODULE_IDS)[number];

export interface LearnModuleSnapshot {
  moduleId: LearnModuleId;
  completedRuns: number;
  totalSeconds: number;
  lastPracticedAt: number | null;
  /** Set whenever the module page is opened, so resume works before a first completion. */
  lastOpenedAt?: number | null;
  /** Context-specific title of the last visit, e.g. "A Minor Pentatonic · Position 2". */
  lastTitle?: string;
  currentFocus?: string;
  savedConfig?: Record<string, unknown>;
  milestones: string[];
}

interface LearnStoreState {
  modules: Partial<Record<LearnModuleId, LearnModuleSnapshot>>;
}

interface LearnProgressEvent {
  completed?: boolean;
  durationSeconds?: number;
  focus?: string;
  milestone?: string;
  metadata?: Record<string, unknown>;
}

const STORAGE_KEY = 'jamcam-learn-v1';

function canUseStorage(): boolean {
  return (
    typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
  );
}

function emptySnapshot(moduleId: LearnModuleId): LearnModuleSnapshot {
  return {
    moduleId,
    completedRuns: 0,
    totalSeconds: 0,
    lastPracticedAt: null,
    milestones: [],
  };
}

function readStore(): LearnStoreState {
  if (!canUseStorage()) return { modules: {} };

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : { modules: {} };
  } catch {
    return { modules: {} };
  }
}

function writeStore(state: LearnStoreState): void {
  if (!canUseStorage()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage can be blocked or full; progress is best-effort.
  }
}

function normalizeSnapshot(moduleId: LearnModuleId): LearnModuleSnapshot {
  return {
    ...emptySnapshot(moduleId),
    ...readStore().modules[moduleId],
    moduleId,
  };
}

export function getLearnSnapshot(moduleId: LearnModuleId): LearnModuleSnapshot {
  return normalizeSnapshot(moduleId);
}

export function getAllLearnSnapshots(): Record<
  LearnModuleId,
  LearnModuleSnapshot
> {
  return LEARN_MODULE_IDS.reduce((acc, moduleId) => {
    acc[moduleId] = normalizeSnapshot(moduleId);
    return acc;
  }, {} as Record<LearnModuleId, LearnModuleSnapshot>);
}

export function getLearnModuleConfig<T extends object>(
  moduleId: LearnModuleId,
  defaults: T
): T {
  const saved = normalizeSnapshot(moduleId).savedConfig ?? {};
  return { ...defaults, ...saved } as T;
}

export function saveLearnModuleConfig(
  moduleId: LearnModuleId,
  config: Record<string, unknown>
): void {
  const state = readStore();
  const previous = normalizeSnapshot(moduleId);

  state.modules[moduleId] = {
    ...previous,
    savedConfig: {
      ...(previous.savedConfig ?? {}),
      ...config,
    },
  };

  writeStore(state);
}

export function recordLearnModuleProgress(
  moduleId: LearnModuleId,
  event: LearnProgressEvent
): LearnModuleSnapshot {
  const state = readStore();
  const previous = normalizeSnapshot(moduleId);
  const milestones = [...previous.milestones];

  if (event.milestone && !milestones.includes(event.milestone)) {
    milestones.push(event.milestone);
  }

  const next: LearnModuleSnapshot = {
    ...previous,
    completedRuns: previous.completedRuns + (event.completed ? 1 : 0),
    totalSeconds: previous.totalSeconds + (event.durationSeconds ?? 0),
    lastPracticedAt: Date.now(),
    currentFocus: event.focus ?? previous.currentFocus,
    milestones: milestones.slice(-12),
    savedConfig: {
      ...(previous.savedConfig ?? {}),
      ...(event.metadata ?? {}),
    },
  };

  state.modules[moduleId] = next;
  writeStore(state);
  if (event.completed) trackJC('practice_run_completed', { moduleId });
  return next;
}

/** Makes this module the dashboard/Learn "continue" target. */
export function markLearnModuleOpened(
  moduleId: LearnModuleId,
  title: string
): void {
  const state = readStore();
  state.modules[moduleId] = {
    ...normalizeSnapshot(moduleId),
    lastOpenedAt: Date.now(),
    lastTitle: title,
  };
  writeStore(state);
}

/** Most recent open or practice time, used to rank "continue" suggestions. */
export function lastActivityAt(snapshot?: LearnModuleSnapshot): number {
  return Math.max(snapshot?.lastPracticedAt ?? 0, snapshot?.lastOpenedAt ?? 0);
}
